import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import * as treeUtils from "utils/treeUtils";
import {
  calculatePartiallyVisible,
  popNodeFromParent,
  updateAncestorsActiveStatus,
  updateSiblingsAfterInsert,
  updateSiblingsAfterPop,
} from "./utils";
import type { Project } from "../projectStore";
import type { NodeRequestItem } from "./utils/createNodeInput";
import { processNodes } from "./utils/mutations";
import type { Node } from "interfaces/schemas";

export type MoveNodeParams = {
  parentId: string | null;
  index: number;
  dragNodes: NodeApi[];
  parentNode: NodeApi | null;
};

export const moveNode = (
  args: MoveNodeParams,
  tree: Node[],
  project: Project,
  language: string,
  setRedrawIndex: (rowIndex: number) => void,
) => {
  const { parentId, index, dragNodes, parentNode } = args;
  if (!project?.id) {
    throw new Error("Project ID is missing. Cannot move node.");
  }
  // Extract the first node being dragged
  const dragNode = dragNodes[0];
  if (!dragNode) {
    throw new Error("No node is being dragged");
  }
  if (dragNode.rowIndex === null) {
    throw new Error("Dragged node does not have a valid row index");
  }
  if (!parentNode) {
    throw new Error("Parent node is missing");
  }
  if (parentNode?.rowIndex === null) {
    throw new Error("Parent node does not have a valid row index");
  }
  if (!dragNode.parent) {
    throw new Error("Dragged node does not have a valid parent");
  }

  // Set the redraw index with the minimum of the dragged node's row index and the parent node's row index
  setRedrawIndex(Math.min(dragNode.rowIndex, parentNode.rowIndex));

  // Count the number of product nodes among the siblings
  let firstValidPosition = 0;
  if (treeUtils.isFolder(dragNode.data) && parentNode) {
    parentNode.children?.forEach((node) => {
      if (
        treeUtils.isAnyProduct(node.data) ||
        treeUtils.isWizard(node.data) ||
        treeUtils.isTroxNetcom(node.data)
      ) {
        firstValidPosition = node.data.position + 1;
      }
    });
  }

  // Determine the new position for the node
  let newPos = index;
  const prevPos = dragNode.data.position;

  if (newPos < firstValidPosition) {
    newPos = parentNode?.children?.length ?? 0;
  }

  // Check if the node is being moved within the same parent
  const isSameParent =
    parentId === dragNode.parent?.id || parentId === dragNode.data.parentId;

  // Adjust the new position if the node is moved within the same parent and the new position is after the previous position
  if (isSameParent && newPos > prevPos) {
    newPos -= 1;
  }

  const nodesToUpdate = new Map();

  const poppedNode = popNodeFromParent(dragNode.parent?.data, dragNode.data.id);

  if (!poppedNode) {
    throw new Error("Node to move not found in parent");
  }

  const { node: removedNode, index: removedIndex } = poppedNode;

  removedNode.position = newPos;
  removedNode.parentId = treeUtils.validateNodeId(parentId, project.id);

  nodesToUpdate.set(removedNode.id, {
    id: removedNode.id,
    position: removedNode.position,
    parentId: treeUtils.validateNodeId(parentId, project.id),
    kind: removedNode.kind,
  });

  updateSiblingsAfterPop(
    dragNode.parent?.data.nodes,
    removedIndex,
    nodesToUpdate,
  );

  const oldParentPartiallyVisible = calculatePartiallyVisible(
    dragNode.parent?.data,
  );

  const oldAncestorsToUpdate: NodeRequestItem[] = [];

  updateAncestorsActiveStatus(
    dragNode.parent,
    oldAncestorsToUpdate,
    project.id,
    {
      prev: {
        active:
          treeUtils.isLeafNode(dragNode.parent.data) &&
          dragNode.parent.data.active,
        partiallyVisible: oldParentPartiallyVisible,
      },
    },
  );

  // Insert the removed node into the new parent at the new position

  parentNode?.data.nodes.splice(newPos, 0, removedNode);

  const newAncestorsToUpdate: NodeRequestItem[] = [];

  const newParentPartiallyVisible = calculatePartiallyVisible(parentNode?.data);

  if (parentNode) {
    updateAncestorsActiveStatus(parentNode, newAncestorsToUpdate, project.id, {
      prev: {
        active: removedNode.active,
        partiallyVisible: newParentPartiallyVisible,
      },
    });
  }

  updateSiblingsAfterInsert(parentNode?.data.nodes, newPos, nodesToUpdate);

  // Execute the mutation to update the node positions in the backend
  const updateNodes = Array.from(nodesToUpdate.values());

  processNodes(
    [
      {
        action: "UPDATE",
        nodes: [...oldAncestorsToUpdate, ...updateNodes],
      },
      {
        action: "UPDATE",
        nodes: newAncestorsToUpdate,
      },
    ],
    project,
    language,
  );

  return [...tree];
};
